<script setup lang="ts">
import { app } from '@/composition/app'
import { GroupState } from '@/composition/groups'
import { Theme } from '@/composition/themes'
import { UserState } from '@/composition/users'
import { replaceBucketUrlWithCdnUrl } from '@/helper/ImageUpload.helper'
import { DEFAULT_GROUP_LOGO } from '@/helper/const'
import { MemberStatus } from '@/models/Groups'
import { useGlobalStore } from '@/store/app'
import { GOKOLLAB_K_LOGO_ONLY } from '@/util/const'
import { externalPathRedirect } from '@/util/redirect'
import { isGokollab } from '@ghl/clientportal-core/init'
import { Home05Icon } from '@gohighlevel/ghl-icons/24/outline'
import { UIAvatar, UITextMdMedium } from '@gohighlevel/ghl-ui'
import { computed, defineAsyncComponent, h, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { truncateString } from '../../../helper'
import GroupSwitcher from '../../GroupSwitcher.vue'
import ShimmerLoader from '../../animations/ShimmerLoader.vue'
import Search from './Search.vue'

const AppSwitcher = defineAsyncComponent(() => import('./AppSwitcher.vue'))
const DarkModeSwitcher = defineAsyncComponent(
  () => import('./DarkModeSwitcher.vue')
)
const NotificationWeb = defineAsyncComponent(
  () => import('./NotificationWeb.vue')
)
const ProfileCard = defineAsyncComponent(() => import('./ProfileCard.vue'))

const LoginModal = defineAsyncComponent(
  () => import('@/components/login/LoginModal.vue')
)

const appStore = useGlobalStore()
const route = useRoute()
const profileCardIsOpen = ref(false)

const appLauncherIsOpen = ref(false)
const notificationListIsOpen = ref(false)
const showLoginModal = ref(false)
const loginPage = ref('LOGIN')

function toggleProfileCard() {
  appLauncherIsOpen.value = false
  notificationListIsOpen.value = false

  profileCardIsOpen.value = !profileCardIsOpen.value
}
function toggleAppLauncher() {
  profileCardIsOpen.value = false
  notificationListIsOpen.value = false
  appLauncherIsOpen.value = !appLauncherIsOpen.value
}
function toggleNotificationList() {
  profileCardIsOpen.value = false
  appLauncherIsOpen.value = false
  notificationListIsOpen.value = !notificationListIsOpen.value
}
const options = ref([])
const selectedGroup = ref()
const selectedGroupObj = ref()
const isLoading = ref(true)

onMounted(async () => {
  //Filter Group which has groupSwitcherVisibility as false
  const groupSwitcher = GroupState.state.groupList
  options.value = groupSwitcher.map((group: any) => {
    const groupOption = {
      id: group.name,
      key: group.slug,
      label: truncateString(group.name, 30),
      imageUrl:
        replaceBucketUrlWithCdnUrl(group?.branding?.logo) || DEFAULT_GROUP_LOGO,
      icon: () => {
        const groupAvatar = h(UIAvatar, {
          style: {
            margin: '8px',
          },
          size: 24,
          class:
            'font-medium text-communities-font-secondary bg-communities-background text-md',
          src: group?.branding?.logo || DEFAULT_GROUP_LOGO,
        })
        return groupAvatar
      },
    }
    if (route.params.groupSlug === group.slug) {
      // groupOption['disabled'] = true
      groupOption['props'] = {
        class:
          'bg-communities-background text-sm text-communities-font-primary rounded-xl',
      }
      selectedGroupObj.value = groupOption
      selectedGroup.value = groupOption.key
    } else {
      groupOption['props'] = {
        class: 'text-sm text-communities-font-primary',
      }
    }
    return groupOption
  })
  isLoading.value = false
})

const isSearchAvailable = computed(() => {
  if (GroupState.state?.groupData?.settings?.groupType === 'Private') {
    if (
      GroupState.state?.role !== null &&
      [
        MemberStatus.BANNED,
        MemberStatus.INVITED,
        MemberStatus.REQUESTED,
      ].includes(GroupState.state?.userStatus)
    ) {
      return false
    } else if (GroupState.state?.role == null) {
      return false
    } else if (appStore.isAuthenticated) {
      return true
    }
  }
  return true
})

const showGroupInfo = computed(() => {
  return (
    GroupState.state.fetchingGroupData ||
    GroupState.state.fetching ||
    !!GroupState.state.groupData?.name
  )
})
</script>

<template>
  <div
    class="lg:mobile-padding box_shadow_top_nav fixed left-0 right-0 top-0 z-50 flex h-[58px] w-full items-center justify-center lg:px-0"
    :class="[
      Theme?.state?.windowGradient
        ? Theme.state.isSideBarBackgroundDark
          ? 'bg-gradient-to-r  from-communities-sidebar-background-gradient from-30% to-communities-sidebar-background'
          : 'bg-gradient-to-r from-communities-sidebar-background from-30% to-communities-sidebar-background-gradient'
        : 'bg-communities-sidebar-background',

      !Theme?.state?.windowGradient
        ? 'border-[1px]  border-l-0 border-t-0 border-solid border-communities-sidebar-border '
        : 'shadow',
    ]"
  >
    <div
      v-if="!isLoading"
      class="h-full w-full items-center justify-between px-4 lg:px-0"
    >
      <div class="flex h-full w-full justify-between">
        <div class="flex h-full justify-start">
          <div class="flex cursor-pointer items-center">
            <div
              id="home-icon-or-gokollab-logo"
              class="flex h-[58px] w-[58px] items-center justify-center border-b-0 border-l-0 border-t-0 border-solid border-communities-sidebar-border"
            >
              <button
                v-if="!isGokollab()"
                class="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border-0 bg-communities-sidebar-background text-communities-sidebar-font-secondary hover:bg-communities-sidebar-fill"
                @click="externalPathRedirect('home', '')"
              >
                <Home05Icon class="homeIcon h-5 w-5" />
              </button>
              <div v-else>
                <img
                  :src="GOKOLLAB_K_LOGO_ONLY"
                  alt="Gokollab Logo"
                  class="h-8 w-8 pt-2"
                  role="button"
                  tabindex="0"
                  @click="externalPathRedirect('discovery', '')"
                  @keydown.enter="externalPathRedirect('discovery', '')"
                />
              </div>
            </div>
            <div id="group-info" class="flex w-[350px] items-center">
              <div
                v-if="showGroupInfo"
                class="flex max-w-full items-center justify-center"
              >
                <div
                  id="group-logo"
                  class="flex cursor-pointer items-center justify-center pl-4"
                >
                  <img
                    :src="
                      replaceBucketUrlWithCdnUrl(
                        GroupState.state.groupData?.branding?.logo
                      ) || DEFAULT_GROUP_LOGO
                    "
                    alt="Group Logo"
                    class="h-8 w-8 rounded-lg"
                  />
                </div>
                <div
                  v-if="GroupState.state.groupData?.name"
                  id="group-name"
                  class="flex h-8 items-center justify-center"
                >
                  <UITextMdMedium
                    class="line-clamp-1 flex h-8 items-center justify-center truncate pl-2 text-communities-sidebar-font-primary"
                  >
                    {{ GroupState.state.groupData?.name }}
                  </UITextMdMedium>
                </div>
                <ShimmerLoader
                  v-else-if="
                    GroupState.state.fetchingGroupData ||
                    GroupState.state.fetching
                  "
                  :repeat="1"
                  :is-loading="true"
                  class-name="absolute inset-0 h-[28px] w-[160px] rounded-md ml-4"
                />
                <GroupSwitcher />
              </div>
            </div>
          </div>
        </div>
        <div
          class="flex h-full w-full items-center"
          :class="
            !appStore.isAuthenticated && !isGokollab()
              ? 'justify-center'
              : 'justify-between'
          "
        >
          <div
            v-if="isSearchAvailable"
            class="flex flex-1 items-center justify-center gap-2"
            :class="{
              'mr-[144px]': !appStore.isAuthenticated && isGokollab(),
            }"
          >
            <Search />
          </div>
        </div>

        <div class="mr-1 flex items-center gap-2">
          <DarkModeSwitcher v-if="UserState.state?.currentUser?.fullName" />
          <AppSwitcher
            v-if="!isGokollab() && UserState.state?.currentUser?.fullName"
            :profile-card-is-open="profileCardIsOpen"
            :app-launcher-is-open="appLauncherIsOpen"
            @update:app-launcher-is-open="toggleAppLauncher"
          />
          <NotificationWeb
            v-if="UserState.state?.currentUser?.fullName"
            :profile-card-is-open="profileCardIsOpen"
            :app-launcher-is-open="appLauncherIsOpen"
            :notification-list-is-open="notificationListIsOpen"
            @update:notification-list-is-open="toggleNotificationList"
          />
          <ProfileCard
            v-if="UserState.state?.currentUser?.fullName"
            :profile-card-is-open="profileCardIsOpen"
            :app-launcher-is-open="appLauncherIsOpen"
            @update:profile-card-is-open="toggleProfileCard"
          />
        </div>
      </div>
    </div>
  </div>
  <LoginModal
    v-if="showLoginModal"
    :page="loginPage"
    :location-data="app.state.locationData"
    :login-confirmation="() => {}"
    :show="showLoginModal"
    @update:show="showLoginModal = false"
  />
</template>
<style scoped>
@media (max-width: 930px) {
  .navbar-container {
    max-width: 930px;
    min-width: 320px;
    width: 100%;
    margin: 0;
  }
}
</style>
